import { zodResolver } from "@hookform/resolvers/zod";
import { LoadingButton } from "@mui/lab";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { AuthError } from "aws-amplify/auth";
import type React from "react";
import { useId, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { cognitoErrorNames, useAuth } from "../hooks/useAuth";
import { type EmailFormValue, emailSchema } from "../schemas";

export const ForgotPW = (): React.ReactElement => {
	const { handleResetPassword } = useAuth();
	const navigate = useNavigate();
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const emailId = useId();

	const {
		register,
		handleSubmit,
		formState: { errors },
		setError,
		clearErrors,
	} = useForm<EmailFormValue>({
		resolver: zodResolver(emailSchema),
	});

	const onSubmit = async (data: EmailFormValue) => {
		clearErrors("root");
		setIsLoading(true);
		try {
			const { nextStep } = await handleResetPassword({
				username: data.email,
			});
			if (nextStep.resetPasswordStep === "CONFIRM_RESET_PASSWORD_WITH_CODE") {
				const params = new URLSearchParams();
				params.set("email", data.email);
				navigate(`/reset-pw?${params.toString()}`);
			}
		} catch (err) {
			if (err instanceof AuthError) {
				switch (err.name) {
					case cognitoErrorNames.ResetPassword.UserNotFound:
						setError("email", { message: "メールアドレスが正しくありません。" });
						break;
					case cognitoErrorNames.ResetPassword.LimitExceeded:
						setError("email", {
							message: "試行回数の上限に達しました。しばらくしてから再度お試しください。",
						});
						break;
					case cognitoErrorNames.ResetPassword.NotAuthorized:
						setError("email", {
							message: "この操作を行う権限がありません。",
						});
						break;
					case cognitoErrorNames.ResetPassword.InvalidParameter:
						setError("email", {
							message: "メールアドレスが確認されていません。",
						});
						break;
					default:
				}
			}
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
				border: "1px solid #d0d7de",
				borderRadius: 1,
				padding: 2,
				backgroundColor: "#f5f5f5",
			}}
		>
			<Typography component="h2" variant="h5">
				パスワードの再設定
			</Typography>
			<Box
				component="form"
				onSubmit={handleSubmit(onSubmit)}
				noValidate
				sx={{
					width: "100%",
					mt: 2.5,
				}}
			>
				<Typography
					variant="body2"
					sx={{
						mb: 1.5,
					}}
				>
					ご登録のメールアドレスを入力してください。パスワード変更に必要な認証コードをお送りします。
				</Typography>
				<TextField
					margin="normal"
					required
					fullWidth
					id={emailId}
					label="メールアドレス"
					autoComplete="email"
					placeholder="email@example.com"
					autoFocus
					sx={{
						backgroundColor: "white",
					}}
					{...register("email")}
					error={Boolean(errors.email)}
				/>
				<Typography variant="caption" color="error" sx={{ display: "block" }}>
					{errors.email?.message}
				</Typography>
				<Typography
					variant="caption"
					sx={{
						display: "block",
						mt: 2,
					}}
				>
					認証コード送信後に自動でページが切り替わります。
				</Typography>
				<LoadingButton
					type="submit"
					fullWidth
					variant="contained"
					loading={isLoading}
					sx={{ mt: 3, mb: 2, fontWeight: "bold" }}
				>
					認証コード送信
				</LoadingButton>
			</Box>
		</Box>
	);
};
